import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFilterPressureDrop } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.filterPressureDrop.v1';

type Fields = {
  deltaP1: string;
  flow1: string;
  flow2: string;
  deltaP2: string;
  loadingMultiplier: string;
};
const EMPTY: Fields = { deltaP1: '', flow1: '', flow2: '', deltaP2: '', loadingMultiplier: '1.00' };

/**
 * Filter Pressure Drop — ΔP₂ = ΔP₁·(Q₂/Q₁)² (square law).
 * Bidirectional: fill any 3 of {ΔP₁, Q₁, Q₂, ΔP₂} to solve the 4th.
 * Loading multiplier is a separate modifier applied to the clean solution.
 */
export default function FilterPressureDropScreen() {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const core = [fields.deltaP1, fields.flow1, fields.flow2, fields.deltaP2];
    const filled = core.filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of {ΔP₁, Q₁, Q₂, ΔP₂} to solve the 4th.'); return; }
    if (filled === 4) { setError('All 4 values are filled — clear one to solve for it.'); return; }

    const dp1 = toImp(fields.deltaP1, 'pressure');
    const q1 = toImp(fields.flow1, 'flow');
    const q2 = toImp(fields.flow2, 'flow');
    const dp2 = toImp(fields.deltaP2, 'pressure');

    if (fields.deltaP2 === '') {
      if (!(dp1 >= 0) || !(q1 > 0) || !(q2 >= 0)) { setError('Q₁ must be > 0; ΔP₁ and Q₂ ≥ 0.'); return; }
      setSolvedFor('deltaP2');
      set({ deltaP2: fromImp(dp1 * Math.pow(q2 / q1, 2), 'pressure', 4) });
    } else if (fields.deltaP1 === '') {
      if (!(q1 > 0) || !(q2 > 0) || !(dp2 >= 0)) { setError('Q₁, Q₂ must be > 0; ΔP₂ ≥ 0.'); return; }
      setSolvedFor('deltaP1');
      set({ deltaP1: fromImp(dp2 * Math.pow(q1 / q2, 2), 'pressure', 4) });
    } else if (fields.flow2 === '') {
      if (!(dp1 > 0) || !(q1 > 0) || !(dp2 >= 0)) { setError('ΔP₁, Q₁ must be > 0.'); return; }
      setSolvedFor('flow2');
      set({ flow2: fromImp(q1 * Math.sqrt(dp2 / dp1), 'flow', 1) });
    } else if (fields.flow1 === '') {
      if (!(dp2 > 0) || !(q2 > 0) || !(dp1 >= 0)) { setError('ΔP₂, Q₂ must be > 0.'); return; }
      setSolvedFor('flow1');
      set({ flow1: fromImp(q2 * Math.sqrt(dp1 / dp2), 'flow', 1) });
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  // Loaded ΔP as derived display
  const dp2Imp = fields.deltaP2 ? toImp(fields.deltaP2, 'pressure') : NaN;
  const mult = parseFloat(fields.loadingMultiplier);
  const loadedImp = Number.isFinite(dp2Imp) && Number.isFinite(mult) && mult >= 1 ? dp2Imp * mult : NaN;

  const result = useMemo(() => [
    fields.deltaP1 && { label: 'ΔP₁ (clean)', value: fields.deltaP1, unit: unit('pressure'), emphasis: solvedFor === 'deltaP1' },
    fields.flow1 && { label: 'Q₁', value: fields.flow1, unit: unit('flow'), emphasis: solvedFor === 'flow1' },
    fields.flow2 && { label: 'Q₂', value: fields.flow2, unit: unit('flow'), emphasis: solvedFor === 'flow2' },
    fields.deltaP2 && { label: 'ΔP₂ (clean)', value: fields.deltaP2, unit: unit('pressure'), emphasis: solvedFor === 'deltaP2' },
    Number.isFinite(loadedImp) && { label: 'ΔP₂ (loaded)', value: fromImp(loadedImp, 'pressure', 4), unit: unit('pressure') },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, solvedFor, loadedImp, fromImp]);

  return (
    <CalcShell
      title="Filter Pressure Drop"
      subtitle="Solve for ΔP or flow using the square law — fill any 3 of 4"
      formula={
        <>
          <FormulaLine>ΔP₂ = ΔP₁ · (Q₂/Q₁)²</FormulaLine>
          <FormulaLine>Loaded ΔP = ΔP_clean · multiplier</FormulaLine>
          <FormulaNote>All ΔP values in {unit('pressure')}; all flows in {unit('flow')}.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: ΔP₂ = ΔP₁·(Q₂/Q₁)²"
      diagram={<DiagramFilterPressureDrop />}
      outputSlots={{
        cfm: fields.flow2 ? toImp(fields.flow2, 'flow') : undefined,
        filter_pressure_drop_iwg: fields.deltaP2 ? toImp(fields.deltaP2, 'pressure') : undefined,
        static_pressure_iwg: Number.isFinite(loadedImp) ? loadedImp : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.flow2) patch.flow2 = fromImp(values.cfm, 'flow', 1);
        if (values.filter_pressure_drop_iwg != null && !fields.deltaP1) patch.deltaP1 = fromImp(values.filter_pressure_drop_iwg, 'pressure', 4);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="ΔP₁ (clean)" value={fields.deltaP1} onChangeText={(v) => set({ deltaP1: v })} rightText={unit('pressure')} maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Q₁" value={fields.flow1} onChangeText={(v) => set({ flow1: v })} rightText={unit('flow')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Q₂" value={fields.flow2} onChangeText={(v) => set({ flow2: v })} rightText={unit('flow')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="ΔP₂ (clean)" value={fields.deltaP2} onChangeText={(v) => set({ deltaP2: v })} rightText={unit('pressure')} maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Loading multiplier" value={fields.loadingMultiplier} onChangeText={(v) => set({ loadingMultiplier: v })} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
}
